import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { driveApi, docsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, FolderPlus, FilePlus, Folder, FileText, Trash2, Share2, ChevronRight, HardDrive, Upload } from 'lucide-react';

export default function DrivePage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState([]);
  const [showNew, setShowNew] = useState(null);
  const [newName, setNewName] = useState('');
  const [showShare, setShowShare] = useState(null);
  const [shareEmail, setShareEmail] = useState('');

  const currentParentId = path.length > 0 ? path[path.length - 1].id : null;

  useEffect(() => { loadFiles(); }, [currentParentId]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const d = await driveApi.list(currentParentId);
      setFiles(d.files || []);
      setShared(d.shared || []);
    } catch { toast.error('Failed to load files'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const item = await driveApi.create({ name: newName, type: showNew, parent_id: currentParentId });
      if (showNew === 'document') {
        const doc = await docsApi.create({ title: newName });
        await driveApi.update(item.file_id, { content: '', doc_id: doc.doc_id });
        navigate(`/docs/${doc.doc_id}`);
      } else {
        setFiles(p => [...p, item]);
      }
      setShowNew(null); setNewName('');
      toast.success(`${showNew === 'folder' ? 'Folder' : 'File'} created`);
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await driveApi.delete(id);
      setFiles(p => p.filter(f => f.file_id !== id));
      toast.success('Deleted');
    } catch (e) { toast.error(e.message); }
  };

  const openFolder = (folder) => {
    setPath(p => [...p, { id: folder.file_id, name: folder.name }]);
  };

  const navigateToBreadcrumb = (index) => {
    setPath(p => p.slice(0, index + 1));
  };

  const handleShare = async () => {
    if (!shareEmail.trim()) return;
    try {
      await driveApi.share(showShare, shareEmail);
      toast.success('Shared!');
      setShowShare(null); setShareEmail('');
    } catch (e) { toast.error(e.message); }
  };

  const openFile = (file) => {
    if (file.type === 'folder') { openFolder(file); return; }
    if (file.doc_id) { navigate(`/docs/${file.doc_id}`); return; }
    toast.info('File preview coming soon');
  };

  const getIcon = (file) => {
    if (file.type === 'folder') return <Folder className="w-5 h-5 text-amber-500" />;
    return <FileText className="w-5 h-5 text-blue-500" />;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="drive-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')}><ArrowLeft className="w-5 h-5" /></Button>
            <HardDrive className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-foreground">Drive</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowNew('folder'); setNewName(''); }} data-testid="new-folder-btn"><FolderPlus className="w-4 h-4 mr-1" /> New Folder</Button>
            <Button onClick={() => { setShowNew('document'); setNewName(''); }} data-testid="new-doc-btn"><FilePlus className="w-4 h-4 mr-1" /> New Document</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm mb-4 text-muted-foreground">
          <button onClick={() => setPath([])} className="hover:text-foreground font-medium">My Drive</button>
          {path.map((p, i) => (
            <React.Fragment key={p.id}>
              <ChevronRight className="w-4 h-4" />
              <button onClick={() => navigateToBreadcrumb(i)} className="hover:text-foreground">{p.name}</button>
            </React.Fragment>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />)}</div>
        ) : files.length === 0 && shared.length === 0 ? (
          <div className="text-center py-20">
            <HardDrive className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">{path.length > 0 ? 'This folder is empty' : 'Your Drive is empty'}</h2>
            <p className="text-muted-foreground mb-6">Create folders and documents to organize your work.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {files.map(file => (
              <div key={file.file_id} onClick={() => openFile(file)}
                className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 cursor-pointer group transition-colors" data-testid={`file-${file.file_id}`}>
                {getIcon(file)}
                <span className="flex-1 text-sm font-medium text-foreground">{file.name}</span>
                <span className="text-xs text-muted-foreground">{new Date(file.updated_at).toLocaleDateString()}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={e => { e.stopPropagation(); setShowShare(file.file_id); }} className="p-1.5 hover:bg-muted rounded"><Share2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
                  <button onClick={e => { e.stopPropagation(); handleDelete(file.file_id); }} className="p-1.5 hover:bg-muted rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                </div>
              </div>
            ))}
            {shared.length > 0 && !currentParentId && (
              <>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mt-6 mb-2">Shared with Me</h3>
                {shared.map(file => (
                  <div key={file.file_id} onClick={() => openFile(file)}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 cursor-pointer transition-colors">
                    {getIcon(file)}
                    <span className="flex-1 text-sm font-medium text-foreground">{file.name}</span>
                    <span className="text-xs text-muted-foreground">{file.owner_name}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </main>

      <Dialog open={showNew !== null} onOpenChange={() => setShowNew(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>New {showNew === 'folder' ? 'Folder' : 'Document'}</DialogTitle></DialogHeader>
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder={`${showNew === 'folder' ? 'Folder' : 'Document'} name`} onKeyDown={e => e.key === 'Enter' && handleCreate()} data-testid="new-item-name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(null)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-item-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Share</DialogTitle></DialogHeader>
          <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Enter user email..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShare(null)}>Cancel</Button>
            <Button onClick={handleShare}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
